import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import {
	createActionGroup,
	deleteActionGroup,
	getActionGroups,
	getAllActionGroups,
	getRolesPreview,
	reorderActionGroups,
	updateActionGroup,
	ActionGroupReorderItem,
} from "../services/action-group.service";
import { ActionGroupStatus } from "../domain/action-group.interface";

export type {
	ActionGroupRequest,
	ActionGroupUpdateRequest,
} from "../services/action-group.service";

export const useGetActionGroups = (
	search?: string,
	page?: number,
	pageSize?: number,
	status?: ActionGroupStatus,
) => {
	return useQuery({
		queryKey: ["action-groups", search, page, pageSize, status],
		queryFn: () => getActionGroups(search, page, pageSize, status),
		staleTime: 5 * 60 * 1000,
		placeholderData: keepPreviousData,
	});
};

export const useGetAllActionGroups = () => {
	return useQuery({
		queryKey: ["action-groups", "all"],
		queryFn: () => getAllActionGroups(),
		staleTime: 5 * 60 * 1000,
	});
};

export const useGetRolesPreview = (enabled = false) => {
	return useQuery({
		queryKey: ["action-groups", "roles-preview"],
		queryFn: () => getRolesPreview(),
		enabled,
		staleTime: 60 * 1000,
	});
};

export const useCreateActionGroup = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: createActionGroup,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["action-groups"] });
		},
	});
};

export const useUpdateActionGroup = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: updateActionGroup,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["action-groups"] });
		},
	});
};

export const useDeleteActionGroup = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: deleteActionGroup,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["action-groups"] });
			queryClient.invalidateQueries({ queryKey: ["actions"] });
		},
	});
};

export const useReorderActionGroups = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			items,
			updatedBy,
		}: {
			items: ActionGroupReorderItem[];
			updatedBy: string;
		}) => reorderActionGroups(items, updatedBy),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["action-groups"] });
		},
	});
};
